<template>
    <div class="ds-main-header-actions">
        <!-- <div class="px-2 cursor-pointer text-gray hover:text-darkblue">
            <icon>
                <IconSettings />
            </icon>
        </div>
        <div class="px-2 cursor-pointer text-gray hover:text-darkblue">
            <icon>
                <IconMailPoint />
            </icon>
        </div> -->
        <div class="ds-header-user-menu-wrapper" v-click-outside="closeMenu">
            <button
                v-if="store.userInfo"
                type="button"
                class="ds-header-user-trigger"
                :class="{ 'is-open': isMenuOpen }"
                aria-haspopup="menu"
                :aria-expanded="isMenuOpen"
                @click="toggleMenu"
            >
                <Avatar :first-name="firstName" :last-name="lastName" :url="avatar" width="32" height="32" />
                <span class="ds-header-user-name">{{ lastName }} {{ firstName }}</span>
                <svg class="ds-header-user-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            <div v-if="isMenuOpen" class="ds-header-user-menu ds-glass-menu z-[60]">
                <button class="ds-glass-menu-item" type="button" @click="openSupportModal">
                    <icon width="16" height="16" vb-size="24" class="ds-glass-menu-item-icon"><IconHelp /></icon>
                    <span class="ds-glass-menu-item-label">Поддержка</span>
                </button>
                <div class="ds-glass-menu-divider" aria-hidden="true"></div>
                <button class="ds-glass-menu-item ds-glass-menu-item-danger" type="button" @click="openLogoutConfirm">
                    <span class="ds-glass-menu-item-label">Выйти из профиля</span>
                </button>
            </div>
        </div>

        <ConfirmDialog
            v-model="isConfirmOpen"
            title="Выйти из профиля?"
            confirm-text="Выйти"
            @confirm="LogOut"
        />

        <modal v-if="isSupportModalOpen" @close="closeSupportModal">
            <!-- RD-189: ширину 520 px задаёт панели правило .ds-glass-dialog:has(> .ds-support-dialog) (40-header-tabs.css). -->
            <div class="ds-support-dialog">
                <button
                    class="absolute right-0 top-0 z-10 flex h-8 w-8 items-center justify-center text-gray hover:text-darkblue"
                    type="button"
                    aria-label="Закрыть форму поддержки"
                    @click="closeSupportModal"
                >
                    <icon width="20" height="20"><IconClose /></icon>
                </button>
                <!-- Текст виден сразу: орбита общего Loader появляется только через 300 ms (место под неё зарезервировано). -->
                <div v-if="supportFormState === 'loading'" class="ds-support-dialog-state">
                    <loader />
                    <p class="ds-support-dialog-loading-text ds-glass-dialog-text" role="status">Загрузка формы…</p>
                </div>
                <p v-else-if="supportFormState === 'error'" class="ds-support-dialog-error ds-glass-dialog-text" role="alert">
                    Не удалось загрузить форму обратной связи. Закройте окно и попробуйте снова
                </p>
                <div ref="feedbackFormContainer" class="ds-support-dialog-body"></div>
            </div>
        </modal>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
// import IconSettings from '../../ui/icons/IconSettings.vue'
// import IconMailPoint from '../../ui/icons/IconMailPoint.vue'
import Avatar from '../Avatar.vue'
import { useAuthStore } from '@/stores/authStore'
import ConfirmDialog from '@/ui/ConfirmDialog.vue'
import IconClose from '@/ui/icons/IconClose.vue'
import IconHelp from '@/ui/icons/IconHelp.vue'

const store = useAuthStore()
const route = useRoute()

// Как прежде в User.vue: пустая строка вместо отсутствующего значения; avatar null → fallback с инициалами.
const firstName = computed(() => store.userInfo?.first_name ?? '')
const lastName = computed(() => store.userInfo?.last_name ?? '')
const avatar = computed(() => store.userInfo?.avatar ?? undefined)

const isMenuOpen = ref(false)
const isConfirmOpen = ref(false)
const isSupportModalOpen = ref(false)
const feedbackFormContainer = ref<HTMLDivElement | null>(null)

const youTrackFormId = '9d92f1ae-9edc-47dc-8083-aee2e89bd8dc'
const youTrackOrigin = 'https://yt.mindm.ru'

// form-entry.js после выполнения публикует window.YTFeedbackForm; getClientJSApi(formUUID) резолвится,
// когда форма получила свои данные (она вызывает setClientJSApi при рендере). Загрузка script — не готовность.
interface YouTrackFeedbackForm {
    getClientJSApi: (formUUID: string) => Promise<unknown>
}

type SupportFormState = 'loading' | 'ready' | 'error'

const supportFormState = ref<SupportFormState>('loading')
// Номер открытия окна: результаты загрузки прежнего (закрытого) открытия игнорируются.
let supportSession = 0

const isCurrentSupportSession = (session: number) => session === supportSession && isSupportModalOpen.value

const failSupportForm = (session: number, reason: unknown) => {
    console.error('Не удалось загрузить форму обратной связи YouTrack', reason)
    if (isCurrentSupportSession(session)) supportFormState.value = 'error'
}

const watchYouTrackFormReady = (session: number) => {
    if (!isCurrentSupportSession(session)) return

    const feedbackForm = (window as Window & { YTFeedbackForm?: YouTrackFeedbackForm }).YTFeedbackForm

    if (!feedbackForm) {
        failSupportForm(session, 'window.YTFeedbackForm отсутствует после загрузки script')
        return
    }

    feedbackForm.getClientJSApi(youTrackFormId).then(
        () => {
            if (isCurrentSupportSession(session)) supportFormState.value = 'ready'
        },
        error => failSupportForm(session, error),
    )
}

const mountYouTrackForm = (session: number) => {
    const container = feedbackFormContainer.value

    if (!container) return

    const script = document.createElement('script')
    script.id = youTrackFormId
    script.dataset.ytUrl = youTrackOrigin
    script.src = `${youTrackOrigin}/static/simplified/form/form-entry.js`
    script.dataset.theme = 'light'
    script.dataset.lang = 'ru'
    script.onload = () => watchYouTrackFormReady(session)
    script.onerror = event => failSupportForm(session, event)

    container.replaceChildren(script)
}

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
    isMenuOpen.value = false
}

const openSupportModal = async () => {
    closeMenu()
    supportSession += 1
    const session = supportSession
    supportFormState.value = 'loading'
    isSupportModalOpen.value = true
    await nextTick()
    if (!isCurrentSupportSession(session)) return
    mountYouTrackForm(session)
}

const closeSupportModal = () => {
    isSupportModalOpen.value = false
    supportSession += 1
}

const openLogoutConfirm = () => {
    closeMenu()
    isConfirmOpen.value = true
}

const LogOut = () => store.logOut()

watch(() => route.fullPath, () => closeMenu())
</script>
